import { useState, useEffect } from 'react';
import useUserDetails from './useUserDetails';
import { AxiosError } from 'axios';
import useErrorModal from './useErrorModal';

interface DataObject<TData> extends Object {
  data: TData;
}

export interface FetchDataOptions {
  dependencies?: Array<string | number | boolean>;
  disableErrorModal?: boolean;
  // does dependencies need to have a value 
  // in order for the fetch function to work
  areDependenciesRequired?: boolean;
}

export type FetchDataFunction<TData> = (eoaId: string, access_token: string) => Promise<DataObject<TData>>;

interface UseFetchDataParams<TData> {
  fetchData: FetchDataFunction<TData>;
  errorMessage: string;
  options?: FetchDataOptions;
  initialState: TData;
}

interface FetchDataState<TData> {
  isPending: boolean;
  error: AxiosError | Error | null;
  data: TData;
}

function useFetchData<T>({ fetchData, errorMessage, options, initialState }: UseFetchDataParams<T>) {
  const { userDetails: { isPending: userDetailsPending, error: userDetailsError, data: userDetails }} = useUserDetails();

  const [requestState, setRequestState] = useState<FetchDataState<T>>({ isPending: true, error: null, data: initialState });

  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const dependencies = options?.dependencies || [];
  const areDependenciesRequired = options?.areDependenciesRequired ?? true;
  
  useEffect(() => {
    if (userDetailsError || (!userDetailsPending && !userDetails)) {
      setRequestState({ isPending: false, data: requestState.data, error: new Error('Request cannot be send due to mising user details data!') });
      return;
    }
    
    let areDependenciesPresent = dependencies.length > 0 ? dependencies.every(dependency => Boolean(dependency)) : true;
    
    // checking if all data needed is available
    // to avoid sending unnecessary requests that will fail
    if (!userDetailsPending && userDetails && (!areDependenciesRequired || areDependenciesPresent)) {
      getData(userDetails.eoaId, userDetails.access_token);
    }
  }, [userDetails, userDetailsPending, userDetailsError, ...dependencies]);

  const getData = async (eoaId: string, access_token: string) => {
    try {
      setRequestState({ ...requestState, isPending: true });

      const response = await fetchData(eoaId, access_token);

      setRequestState({ isPending: false, data: response.data, error: null });
    } catch (error) {
      setRequestState({ isPending: false, data: requestState.data, error: error as AxiosError });
      if (options?.disableErrorModal) return;

      handleOpenErrorModal(errorMessage, error as AxiosError);
    }
  };

  const refreshData = () => {
    if (userDetailsError || (!userDetailsPending && !userDetails)) {
      setRequestState({ isPending: false, data: requestState.data, error: new Error('Request cannot be send due to mising user details data!') });
      return;
    }
    
    if (userDetails) getData(userDetails?.eoaId, userDetails?.access_token);
  };

  const setData = (updater: (prev: T) => T) => {
    setRequestState(prev => ({
      ...prev,
      data: updater(prev.data),
    }));
  };
  
  return {
    ...requestState,
    setData,
    refreshData
  };  
}

export default useFetchData;